import { useCallback, useEffect, useState } from "react";
import { apiClient } from "../../utils/http";
import { todayISO } from "../../utils/format";
import { SCHEDULE_DELETE_CONFIRM } from "../../utils/constants";

/*
 * Everything behind the Calendar & Schedule: the summary (stats, reminder
 * alerts, the dashboard widget's items), which of its modals are open, and
 * the event actions. `version` bumps after any change so open views refetch.
 */
export default function useSchedule(showToast) {
  const [summary, setSummary] = useState(null);
  const [version, setVersion] = useState(0);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayDate, setDayDate] = useState(null);
  const [eventForm, setEventForm] = useState(null);

  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get("schedule/summary", { params: { today: todayISO() } })
      .then((res) => !cancelled && setSummary(res.data))
      .catch((err) => !cancelled && showToast(err.message, "error"));
    return () => {
      cancelled = true;
    };
  }, [version, showToast]);

  const openDay = useCallback((date) => {
    setSelectedDate(date);
    setDayDate(date);
  }, []);

  const closeDay = useCallback(() => setDayDate(null), []);

  /* With a date (a widget item was clicked) the day's details open on top. */
  const openCalendar = useCallback(
    (date) => {
      setCalendarOpen(true);
      if (typeof date === "string") openDay(date);
    },
    [openDay]
  );

  const closeCalendar = useCallback(() => {
    setCalendarOpen(false);
    setDayDate(null);
    setSelectedDate(null);
  }, []);

  const openEventForm = useCallback(
    (type = "job", date = null, event = null) => {
      setEventForm({ type, date: date || selectedDate || todayISO(), event });
    },
    [selectedDate]
  );

  const closeEventForm = useCallback(() => setEventForm(null), []);

  const toggleEvent = useCallback(
    async (item) => {
      try {
        await apiClient.post(`schedule/toggle/${item.id}`);
        refresh();
      } catch (err) {
        showToast(err.message, "error");
      }
    },
    [refresh, showToast]
  );

  const deleteEvent = useCallback(
    async (item) => {
      if (!window.confirm(SCHEDULE_DELETE_CONFIRM)) return;
      try {
        const res = await apiClient.delete(`schedule/delete/${item.id}`);
        showToast(res.data.message || "Schedule item removed", "info");
        refresh();
      } catch (err) {
        showToast(err.message, "error");
      }
    },
    [refresh, showToast]
  );

  return {
    summary,
    version,
    refresh,
    calendarOpen,
    openCalendar,
    closeCalendar,
    selectedDate,
    dayDate,
    openDay,
    closeDay,
    eventForm,
    openEventForm,
    closeEventForm,
    toggleEvent,
    deleteEvent,
  };
}
